import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Input, Button, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { API_URL } from "../../utils/constants";
import { submitSessionRefreshCaptcha } from "../../apis/patientService";

const BLUE = "rgb(0, 84, 157)";
const ALERT_BG = "#F5E6C8";
const ALERT_TEXT = "#5C4A2A";
const TIME_COLOR = "#A67C2D";

interface SessionRefreshPayload {
  title?: string;
  subText?: string;
  captcha?: string;
  timeLeft?: string;
  hospitalId?: number;
  hospitalName?: string;
  schemeId?: number;
  alreadyDoneBySomeone?: boolean;
}

const parseTimeLeftToSeconds = (timeLeft?: string): number => {
  if (!timeLeft) return 0;
  const parts = timeLeft.trim().split(":").map(Number);
  if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
};

const formatSecondsToTime = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, totalSeconds);
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const CghsSessionRefreshModalAlert: React.FC = () => {
  const { selectedHospital, availableHospitals } = useSelector(
    (state: RootState) => state.hospital,
  );
  const { auth } = useSelector((state: RootState) => state);
  const [visible, setVisible] = useState(false);
  const [payload, setPayload] = useState<SessionRefreshPayload | null>(null);
  const [captchaCode, setCaptchaCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const isOpenRef = useRef(false);

  const hospitalName = useMemo(() => {
    if (payload?.hospitalName) return payload.hospitalName;

    const hospitalId = payload?.hospitalId ?? selectedHospital?.value;
    if (!hospitalId || hospitalId === -1) return "";

    if (selectedHospital?.value === hospitalId) {
      return selectedHospital.label;
    }

    return (
      availableHospitals?.find((hospital) => hospital.value === hospitalId)
        ?.label ?? ""
    );
  }, [payload, selectedHospital, availableHospitals]);

  const displayTime = formatSecondsToTime(remainingSeconds);
  const handleCancelRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!selectedHospital?.value || selectedHospital?.value === -1) return;
    
    const url = `${API_URL}/api/sse/subscribe/cghs-session-refresh-hsp-${selectedHospital.value}`;
    const eventSource = new EventSource(url);

    const handleSseEvent = (event: MessageEvent) => {
      try {
        const data: SessionRefreshPayload = JSON.parse(event.data);
        if (data?.alreadyDoneBySomeone) {
          handleCancelRef.current();
          return;
        }
        // Open once with SSE payload
        if (data?.captcha && !isOpenRef.current) {
          isOpenRef.current = true;
          setPayload(data);
          setRemainingSeconds(parseTimeLeftToSeconds(data.timeLeft));
          setCaptchaCode("");
          if(auth?.role?.role === "Mid Office Role") {
            setVisible(true);
          }
        }
      } catch (error) {
        console.error("Error parsing CGHS session refresh SSE:", error);
      }
    };

    eventSource.addEventListener(`cghs-session-refresh-hsp-${selectedHospital.value}`, handleSseEvent);
    eventSource.onmessage = handleSseEvent;

    eventSource.onerror = (err) => {
      console.error("CGHS session refresh SSE Error:", err);
      eventSource.close();
    };

    return () => eventSource.close();
  }, [selectedHospital?.value]);

  useEffect(() => {
    if (!visible || !payload?.timeLeft) return;

    const initialSeconds = parseTimeLeftToSeconds(payload.timeLeft);
    setRemainingSeconds(initialSeconds);

    const intervalId = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          handleCancelRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [visible, payload?.timeLeft]);

  const handleCancel = () => {
    isOpenRef.current = false;
    setVisible(false);
    setCaptchaCode("");
    setPayload(null);
    setRemainingSeconds(0);
  };

  handleCancelRef.current = handleCancel;

  const handleSubmit = async () => {
    if (!captchaCode.trim()) {
      message.warning("Please enter captcha");
      return;
    }

    setSubmitting(true);
    try {
      await submitSessionRefreshCaptcha({
        hospitalId: payload?.hospitalId ?? selectedHospital?.value ?? 0,
        schemeId: payload?.schemeId ?? 96,
        captchaCode: captchaCode.trim(),
      });
      message.success("Session refreshed successfully");
      handleCancel();
    } catch (error) {
      console.error("CGHS session refresh submit failed:", error);
      message.error("Failed to refresh session. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderAlertText = () => {
    const timeHighlight = (
      <span style={{ fontWeight: 700, color: TIME_COLOR }}>{displayTime}</span>
    );

    if (payload?.subText && payload?.timeLeft) {
      if (payload.subText.includes(payload.timeLeft)) {
        const [before, after] = payload.subText.split(payload.timeLeft);
        return (
          <>
            {before}
            {timeHighlight}
            {after}
          </>
        );
      }
    }

    if (payload?.subText && /\d+:\d{2}/.test(payload.subText)) {
      const parts = payload.subText.split(/\d+:\d{2}/);
      return parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && timeHighlight}
        </React.Fragment>
      ));
    }

    return (
      <>
        Your session will expire in {timeHighlight}. To extend the session,
        please enter captcha below and click on submit.
      </>
    );
  };

  return (
    <Modal
      open={visible}
      centered
      closable={false}
      maskClosable={false}
      width={660}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 16,
          }}
        >
          <span
            style={{
              color: "#fff",
              fontSize: 16,
              fontWeight: 500,
              textTransform: "none",
            }}
          >
            {payload?.title || "Session time out notification"}
          </span>
          {hospitalName && (
            <span
              style={{
                color: "#fff",
                fontSize: 14,
                fontWeight: 400,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              {hospitalName?.toUpperCase()}
            </span>
          )}
        </div>
      }
      onCancel={handleCancel}
      styles={{
        header: {
          background: BLUE,
          marginBottom: 0,
          padding: "12px 20px",
          borderRadius: "8px 8px 0 0",
        },
        body: {
          padding: "20px 24px 8px",
          textTransform: "none",
        },
        footer: {
          borderTop: "1px solid #e8e8e8",
          padding: "16px 24px",
          textAlign: "center",
        },
        content: {
          padding: 0,
          borderRadius: 8,
          overflow: "hidden",
        },
      }}
      footer={[
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          style={{
            background: BLUE,
            borderColor: BLUE,
            minWidth: 120,
            fontWeight: 600,
            letterSpacing: 0.5,
            marginRight: 12,
            textTransform: "uppercase",
          }}
        >
          Submit
        </Button>,
        <Button
          key="cancel"
          onClick={handleCancel}
          style={{
            minWidth: 120,
            fontWeight: 600,
            letterSpacing: 0.5,
            color: BLUE,
            borderColor: BLUE,
            background: "#fff",
            textTransform: "uppercase",
          }}
        >
          Cancel
        </Button>,
      ]}
    >
      <div
        style={{
          background: ALERT_BG,
          padding: "14px 16px",
          borderRadius: 4,
          marginBottom: 24,
          color: ALERT_TEXT,
          fontSize: 14,
          lineHeight: 1.6,
        }}
      >
        {renderAlertText()}
      </div>

      <div style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 14, color: "#333" }}>Captcha</span>
        <span style={{ color: "#ff4d4f", marginLeft: 2 }}>*</span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        <Input
          value={captchaCode}
          onChange={(e) => setCaptchaCode(e.target.value)}
          placeholder="Enter captcha"
          onPressEnter={handleSubmit}
          style={{
            flex: 1,
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
            textTransform: "none",
          }}
        />
        {payload?.captcha && (
          <img
            src={
              payload.captcha.startsWith("data:")
                ? payload.captcha
                : `data:image/jpeg;base64,${payload.captcha}`
            }
            alt="Captcha"
            style={{
              height: 40,
              width: 120,
              objectFit: "contain",
              borderLeft: "1px solid #d9d9d9",
              borderRight: "1px solid #d9d9d9",
              background: "#fff",
              padding: "2px 6px",
            }}
          />
        )}
        <Button
          type="text"
          icon={<ReloadOutlined style={{ fontSize: 16, color: "#666" }} />}
          onClick={() => setCaptchaCode("")}
          style={{
            width: 40,
            height: 40,
            borderRadius: 0,
            border: "none",
          }}
        />
      </div>
    </Modal>
  );
};

export default CghsSessionRefreshModalAlert;
